// Display order for First Underwriting -> Data Extractor. Calculated rows (including
// Runoff and Liquid Value) are always listed; missing values stay empty.

export const FINANCIAL_SNAPSHOT_TABLE_META_KEYS = [
  'updatedAt',
  'documentSummary',
  'attachmentSummaries',
  'cohortData',
];

// Narrative fields from the latest extract run — not shown in the parameter table.
export const QCF_NARRATIVE_FIELD_KEYS = ['qcfDocumentSummary', 'qcfExplanation'];
export const QCF_HISTORY_LIST_FIELDS = QCF_NARRATIVE_FIELD_KEYS;

// Compound QCF payloads that are not a single table-row value.
export const QCF_COMPOUND_TABLE_SKIP_FIELDS = [
  'qcfMarketingPlan',
  'qcfSelectedFiles',
  'qcfCohortSheet',
  'qcfDriveExtractStatus',
];

// Read-only extract run metadata — shown as bullets above the parameter table.
export const FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS = [
  'qcfNeedsClarification',
  'qcfFilesUsed',
  'qcfExtractedAt',
  'qcfPromptVersion',
];

/** @deprecated use FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS */
export const FINANCIAL_SNAPSHOT_META_FIELD_KEYS = FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS;

export const FINANCIAL_SNAPSHOT_SOURCE_FIELD_KEYS = [
  'qcfTotalAssets',
  'qcfTotalLiabilities',
  'qcfSafe',
  'qcfDebt',
  'qcfTotalOpex',
  'qcfMarketingSpend',
  'qcfOpex',
  'qcfNetBurn',
  'qcfBeginCash',
  'qcfGpm',
  'qcfRevenueGrowthYoy',
];

export const FINANCIAL_SNAPSHOT_CALCULATED_FIELD_KEYS = [
  'qcfCashBalance',
  'qcfBalanceSheet',
  'qcfRunway',
  'qcfRunoff',
  'qcfLiquidValue',
];

// Always derived from source parameters on Data Extractor — not editable.
export const FINANCIAL_SNAPSHOT_READ_ONLY_CALCULATED_KEYS = [
  ...FINANCIAL_SNAPSHOT_CALCULATED_FIELD_KEYS,
];

export const FINANCIAL_SNAPSHOT_FIELD_LABELS = {
  qcfNeedsClarification: 'Needs clarification',
  qcfFilesUsed: 'Accounting files used',
  qcfExtractedAt: 'Extracted at',
  qcfPromptVersion: 'Prompt version',
  qcfTotalAssets: 'Total Assets',
  qcfTotalLiabilities: 'Total Liabilities',
  qcfSafe: 'Safe',
  qcfDebt: 'Debt',
  qcfTotalOpex: 'Total OPEX',
  qcfMarketingSpend: 'Marketing Spend',
  qcfOpex: 'OPEX',
  qcfNetBurn: 'Net Burn',
  qcfBeginCash: 'Beginning Cash',
  qcfGpm: 'Gross Profit Margin',
  qcfRevenueGrowthYoy: 'Revenue Growth YOY',
  qcfCashBalance: 'Cash Balance',
  qcfBalanceSheet: 'Balance Sheet',
  qcfRunway: 'Runway',
  qcfRunoff: 'Runoff',
  qcfLiquidValue: 'Liquid Value',
};

export const FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS = {
  qcfOpex:
    'Operating expenses excluding marketing: Total OPEX − Marketing Spend, averaged over the most recent 3 completed months.',
  qcfTotalOpex:
    'Total operating expenses (the P&L Total Operating Expenses row), averaged over the most recent 3 completed months.',
  qcfMarketingSpend:
    'Marketing and advertisement spend (sum of the detail marketing/advertising rows), averaged over the most recent 3 completed months. Subtracted from Total OPEX to derive OPEX.',
  qcfDebt: 'Total outstanding debt / loans payable, excluding SAFEs and equity.',
  qcfTotalAssets: 'Total assets from the balance sheet.',
  qcfTotalLiabilities: 'Total liabilities from the balance sheet.',
  qcfSafe:
    'Whether the company has issued SAFE (Simple Agreement for Future Equity) notes, and the total outstanding SAFE principal if disclosed.',
  qcfNetBurn:
    'Average month-over-month change in cash balance over up to the last 4 reported months (uses fewer if under 4 are available; needs at least 2). Negative when cash is declining, positive when growing.',
  qcfBeginCash: 'Cash at the start of the most recent reported period.',
  qcfGpm:
    'Gross profit margin as a percentage for the most recent period: (Revenue - COGS) / Revenue * 100.',
  qcfRevenueGrowthYoy:
    '(Revenue last full month − revenue same month prior year) ÷ revenue same month prior year × 100.',
  qcfCashBalance: 'Beginning cash ÷ monthly OPEX.',
  qcfBalanceSheet: 'Total assets ÷ (total liabilities − SAFE).',
  qcfRunway:
    'Beginning cash ÷ net burn (average absolute monthly cash change over the last 4 months).',
  qcfRunoff:
    '12-month predicted runoff from the processed cohort sheet ÷ monthly OPEX.',
  qcfLiquidValue:
    '(Beginning cash + 12-month predicted runoff − debt) ÷ monthly OPEX.',
  cohortData:
    'Customer or revenue cohort data extracted from the documents, reported as ROAS or revenue by cohort and period.',
};

const camelToLabel = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

export function snapshotFieldLabel(key) {
  return FINANCIAL_SNAPSHOT_FIELD_LABELS[key] || camelToLabel(key);
}

export function isSnapshotTableMetricKey(key) {
  return (
    key.startsWith('qcf') &&
    !QCF_NARRATIVE_FIELD_KEYS.includes(key) &&
    !QCF_COMPOUND_TABLE_SKIP_FIELDS.includes(key) &&
    !FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS.includes(key) &&
    !FINANCIAL_SNAPSHOT_TABLE_META_KEYS.includes(key)
  );
}

export function formatExtractInfoValue(key, raw) {
  if (raw == null || raw === '') {
    return null;
  }
  switch (key) {
    case 'qcfNeedsClarification':
      if (raw === true) {
        return 'Yes — review accounting inputs';
      }
      if (raw === false) {
        return 'No';
      }
      return null;
    case 'qcfFilesUsed': {
      const count = Number(raw);
      if (!Number.isFinite(count)) {
        return null;
      }
      return String(count);
    }
    case 'qcfExtractedAt':
      return String(raw);
    case 'qcfPromptVersion':
      return String(raw);
    default:
      return String(raw);
  }
}

export function extractInfoBullets(snapshot) {
  if (!snapshot) {
    return [];
  }
  return FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS.map((key) => {
    const value = formatExtractInfoValue(key, snapshot[key]);
    if (value == null) {
      return null;
    }
    return { key, label: snapshotFieldLabel(key), value };
  }).filter(Boolean);
}

function presentKeysInOrder(snapshot, orderedKeys) {
  if (!snapshot) {
    return [];
  }
  return orderedKeys.filter((key) => snapshot[key] != null);
}

export function groupSnapshotMetricKeys(snapshot) {
  const hasProcessedCohort = Boolean(
    snapshot?.qcfCohortSheet?.value?.cohorts?.length
  );
  const allKeys = snapshot
    ? Object.keys(snapshot).filter(isSnapshotTableMetricKey)
    : [];
  const known = new Set([
    ...FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS,
    ...FINANCIAL_SNAPSHOT_SOURCE_FIELD_KEYS,
    ...FINANCIAL_SNAPSHOT_CALCULATED_FIELD_KEYS,
  ]);
  return {
    extractInfoKeys: presentKeysInOrder(snapshot, FINANCIAL_SNAPSHOT_EXTRACT_INFO_KEYS),
    sourceKeys: [...FINANCIAL_SNAPSHOT_SOURCE_FIELD_KEYS],
    extraKeys: allKeys.filter((key) => !known.has(key)).sort(),
    calculatedKeys: [...FINANCIAL_SNAPSHOT_CALCULATED_FIELD_KEYS],
    hasProcessedCohort,
  };
}
